import { isEmpty } from 'lodash';

// TYPES
import { AppThunk } from 'redux/type';

// BASE ACTION
import { httpAction } from 'redux/base/action';

// MODEL
import { Contact } from 'model';

// TYPE
import { Callback } from 'interface';

// APP ACTIONS
import { setAppMessage } from 'redux/app/actions';

// HELPERS
import { pushData, formatItemArrByFn, changeValueOfItemFromArr, removeData } from 'helpers/array';
import { isNullText } from 'helpers/string/nullText';

// CONSTANT
import { relationshipList } from 'constant/data';

// SLICE
import { contactSlice } from './slice';

// SELECTOR
import { selectManyContacts } from './selector';

// ACTIONS
const { changeContactLoading, setManyContacts, setContact } = contactSlice.actions;

const reformatDataForRedux = (contact: Contact) => {
  const data = {
    ...contact
  };

  Object.keys(data).forEach((v) => {
    if (!data[v] && v !== 'payer' && typeof data[v] !== 'boolean') {
      data[v] = '';
    }

    if (!data[v] && v === 'payer') {
      data[v] = false;
    }

    if (isNullText(data[v])) {
      data[v] = '';
    }
  });

  return data;
};

// Reformat data to submit api
const reformatSubmitData = (contact: Contact): Contact => {
  let data = { ...contact };

  if (data.relationship === 'Others') {
    data = {
      ...data,
      otherRelationship: data.otherRelationship
    };
  }

  if (data.relationship !== 'Others') {
    data = {
      ...data,
      otherRelationship: ''
    };
  }

  if (isEmpty(contact.homeNumber)) {
    data = {
      ...data,
      homeNumber: null
    };
  }

  return data;
};

const getManyContacts =
  (apiUrl: string, callback?: Callback<Contact[]>): AppThunk =>
  async (dispatch) => {
    dispatch(changeContactLoading(true));

    dispatch(
      httpAction<unknown, unknown, Contact[]>(
        {
          method: 'GET',
          url: apiUrl,
          needAuth: true
        },
        async (res) => {
          dispatch(changeContactLoading(false));
          const data = res.data || [];

          dispatch(setManyContacts(formatItemArrByFn<Contact>(data || [], reformatDataForRedux)));

          if (callback) {
            callback(data);
          }
        }
      )
    );
  };

const createNewContact =
  (contact: Contact, apiUrl: string, callback?: Callback<Contact>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(changeContactLoading(true));

    const currentContacts = selectManyContacts(getState());
    const updateContactParam = reformatSubmitData(contact);

    dispatch(
      httpAction<Contact, unknown, Contact>(
        {
          method: 'POST',
          url: apiUrl,
          data: updateContactParam,
          needAuth: true
        },
        async (res) => {
          dispatch(changeContactLoading(false));

          if (res.data) {
            dispatch(
              setAppMessage({
                type: 'success',
                content: 'Created contact successfully'
              })
            );

            dispatch(setManyContacts(pushData<Contact>(currentContacts, res.data, reformatDataForRedux)));
          }

          if (callback) {
            callback(res.data);
          }
        }
      )
    );
  };

const updateContact =
  (contact: Contact, apiUrl: string, callback?: Callback<Contact>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(changeContactLoading(true));

    const currentContacts = selectManyContacts(getState());
    const immutableContacts = changeValueOfItemFromArr<Contact>(currentContacts, contact);
    const updateContactParam = reformatSubmitData(contact);

    dispatch(
      httpAction<Contact, unknown, Contact>(
        {
          method: 'PUT',
          url: `${apiUrl}/${contact.id}`,
          data: updateContactParam,
          needAuth: true
        },
        async (res) => {
          dispatch(changeContactLoading(false));

          if (res.data) {
            dispatch(
              setAppMessage({
                type: 'success',
                content: 'Updated contact successfully'
              })
            );

            dispatch(setManyContacts(immutableContacts));
          }

          if (callback) {
            callback(res.data);
          }
        }
      )
    );
  };

const deleteContact =
  (contact: Contact, apiUrl: string, callback?: Callback<boolean>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(changeContactLoading(true));

    const currentContacts = selectManyContacts(getState());
    const immutableContacts = removeData<Contact>(currentContacts, contact);

    dispatch(
      httpAction<Contact, unknown, boolean>(
        {
          method: 'DELETE',
          url: `${apiUrl}/${contact.id}`,
          needAuth: true
        },
        async (res) => {
          dispatch(changeContactLoading(false));

          if (res.httpCode === 200) {
            dispatch(
              setAppMessage({
                type: 'success',
                content: 'Deleted contact successfully'
              })
            );

            dispatch(setManyContacts(immutableContacts));
          }

          if (callback) {
            callback(res.httpCode === 200);
          }
        }
      )
    );
  };

// ----------------------------- EMERGENCY CONTACT  -----------------------------------------

export const getEmergencyContact =
  (apiUrl: string, callback?: Callback<Contact>): AppThunk =>
  async (dispatch) => {
    dispatch(changeContactLoading(true));

    dispatch(
      httpAction<Contact, unknown, Contact>(
        {
          method: 'GET',
          url: apiUrl,
          needAuth: true,
          hideToast: true
        },
        async (res) => {
          dispatch(changeContactLoading(false));

          if (callback) {
            callback(res.data ? reformatDataForRedux(res.data) : res.data);
          }
        }
      )
    );
  };

export const setContactThunk =
  (data: Contact, isInitialData?: boolean): AppThunk =>
  async (dispatch) => {
    const cloneData = { ...data };

    const isRelationShipBelongedList = relationshipList.find((v) => v.value === data.relationship);

    if (isInitialData) {
      dispatch(setContact(data));
    }

    if (!isInitialData && isRelationShipBelongedList) {
      dispatch(setContact(cloneData));
    }

    if (!isInitialData && !isRelationShipBelongedList) {
      cloneData.otherRelationship = cloneData.relationship;
      cloneData.relationship = 'Others';

      dispatch(setContact(cloneData));
    }
  };

export { getManyContacts, createNewContact, updateContact, deleteContact };
